import { Injectable } from "@angular/core";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { AppIdService } from "@bitwarden/common/abstractions/appId.service";
import { CryptoFunctionService } from "@bitwarden/common/abstractions/cryptoFunction.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { EncryptService } from "@bitwarden/common/abstractions/encrypt.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { Utils } from "@bitwarden/common/misc/utils";
import { SymmetricCryptoKey } from "@bitwarden/common/models/domain/symmetric-crypto-key";
import { KeysRequest } from "@bitwarden/common/models/request/keys.request";
import { RegisterFirstDeviceRequest } from "@bitwarden/common/models/request/register-first-device.request";
import { UploadRecoveryKeyRequest } from "@bitwarden/common/models/request/upload-recovery-key.request";

import { DeviceTrustService } from "./device-trust.service";

/**
 * Orchestrates TDE's "first device" flow (GOOGLE_SSO_PLAN.md, Phase 1-2, first-device-only
 * scope): generates a UserKey, self-approves it under this device's own keypair, creates
 * the account's RSA keypair, and produces + uploads a recovery key. Replaces the
 * password-based `set-password` flow for a brand-new Google SSO user (`IsFirstDevice: true`).
 *
 * This device's `getAppId()` identifier is sent to `POST /devices` so a later login from
 * the same device is recognized as returning rather than treated as brand-new — the
 * backend looks it up via `device_identifier` in `_google_login` and returns this device's
 * wrapped `EncryptedUserKey`. See `ReturningDeviceUnlockService` for the login-time consumer.
 */
@Injectable({
  providedIn: "root",
})
export class FirstDeviceSetupService {
  // In-memory only (unlike router state, which Angular restores on refresh/back), so a
  // direct URL visit or a reload can never re-run setup and overwrite the account keys.
  private armed = false;

  constructor(
    private deviceTrustService: DeviceTrustService,
    private cryptoService: CryptoService,
    private cryptoFunctionService: CryptoFunctionService,
    private encryptService: EncryptService,
    private apiService: ApiService,
    private appIdService: AppIdService,
    private stateService: StateService,
    private logService: LogService,
  ) {}

  /** Called by `LoginComponent` only when the login response returned `isFirstDevice: true`. */
  armSetup(): void {
    this.armed = true;
  }

  /** Read-only check for the route guard; `consumeArmedSetup()` is what disarms. */
  isSetupArmed(): boolean {
    return this.armed;
  }

  /** Returns whether setup was armed by the login that navigated here, and disarms it. */
  consumeArmedSetup(): boolean {
    const armed = this.armed;
    this.armed = false;
    return armed;
  }

  /**
   * Runs the full first-device setup and returns the recovery key for display. Must be
   * followed by `acknowledgeRecoveryKey()` once the user confirms they've saved it.
   */
  async setUpFirstDevice(deviceName?: string): Promise<string> {
    const userKey = await this.deviceTrustService.generateUserKey();

    const { publicKey, publicKeySpki } = await this.deviceTrustService.getOrCreateDeviceKeyPair();

    // Self-approval: this is the only device, so it wraps the UserKey with its own
    // public key rather than waiting on another device to approve it.
    const wrappedForDevice = await this.deviceTrustService.wrapUserKey(userKey, publicKey);
    const deviceIdentifier = await this.appIdService.getAppId();
    await this.apiService.registerFirstDevice(
      new RegisterFirstDeviceRequest(
        Utils.fromBufferToB64(publicKeySpki),
        wrappedForDevice.encryptedString,
        deviceIdentifier,
        deviceName,
      ),
    );

    // The account's RSA keypair (org sharing, etc.) is encrypted directly with the
    // UserKey — the same role a master-key-derived key normally plays, minus the master
    // password. `/accounts/keys` needs no password, only the Bearer token already held
    // from the Google login response.
    const [publicKeyB64, encryptedPrivateKey] = await this.cryptoService.makeKeyPair(userKey);
    await this.apiService.postAccountKeys(
      new KeysRequest(publicKeyB64, encryptedPrivateKey.encryptedString),
    );

    const recoveryKey = await this.generateAndUploadRecoveryKey(userKey);

    // Unlocks the vault for the rest of this session using the UserKey directly, in place
    // of a master-password-derived key. setKey() is what AuthGuard/getAuthStatus's
    // hasKeyInMemory() check reads (via stateService.getCryptoMasterKey()) — skipping it
    // left the app thinking the vault was still locked even though decryption worked, so
    // it kept redirecting to the lock/unlock-with-password screen after this flow finished.
    // No setEncKey() call: with none set, getKeyForUserEncryption() falls back to getKey()
    // directly (its own "legacy: encryption used to be done with the user key" path) —
    // exactly the role UserKey plays here, so no separate wrapped-encKey layer is needed.
    await this.cryptoService.setKey(userKey);
    await this.cryptoService.setEncPrivateKey(encryptedPrivateKey.encryptedString);
    // Best-effort: the recovery key is already uploaded, so a failed IndexedDB write must not
    // stop it from being shown. Without the marker this device just can't approve others yet.
    try {
      await this.deviceTrustService.markTrustedFor(await this.stateService.getUserId());
    } catch (e) {
      this.logService.error(e);
    }

    return recoveryKey;
  }

  async acknowledgeRecoveryKey(): Promise<void> {
    await this.apiService.acknowledgeRecoveryKey();
  }

  private async generateAndUploadRecoveryKey(userKey: SymmetricCryptoKey): Promise<string> {
    const recoveryKeyBytes = await this.cryptoFunctionService.randomBytes(32);
    const recoveryKeyB64 = Utils.fromBufferToB64(recoveryKeyBytes);

    // Same "expand arbitrary key material into a 64-byte enc+mac key" idiom as
    // CryptoService.makeSendKey, with its own domain-separation label.
    const stretchedKey = await this.cryptoFunctionService.hkdf(
      recoveryKeyBytes,
      "gloss-recovery-key",
      "recovery",
      64,
      "sha256",
    );
    const recoverySymmetricKey = new SymmetricCryptoKey(stretchedKey);

    const wrappedForRecovery = await this.encryptService.encrypt(userKey.key, recoverySymmetricKey);
    await this.apiService.uploadRecoveryKey(
      new UploadRecoveryKeyRequest(wrappedForRecovery.encryptedString),
    );

    return recoveryKeyB64;
  }
}
